import { useCallback, useEffect, useRef, useState } from 'react'

import { animateScroll as scroll, scroller } from 'react-scroll'

import { Direction } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'

export const useTabPointerNavigation = ({
  focusNode,
  tabIndex,
  setDefaultFocusChild,
}: {
  focusNode: FocusNode | null
  tabIndex: number
  setDefaultFocusChild: (focus: number) => void
}) => {
  const focusTimeoutId = useRef<NodeJS.Timeout>()
  const mouseOverIntervalId = useRef(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  useEffect(() => {
    if (!focusNode || focusNode.isFocused) {
      return
    }

    const focusedTab = focusNode.children[tabIndex]
    if (focusedTab) {
      focusTimeoutId.current = setTimeout(() => {
        setDefaultFocusChild(tabIndex)
        scroller.scrollTo(focusedTab, scrollOptions.TabScroll.tabsNav)
      }, 1500)
    }

    return () => {
      clearTimeout(focusTimeoutId.current)
    }
  }, [focusNode?.isFocused, tabIndex])

  useEffect(() => {
    const tabContainerElement = focusNode?.elRef.current
    if (!tabContainerElement) {
      return
    }

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = tabContainerElement
      const maxScrollLeft = scrollWidth - clientWidth

      setCanScrollPrev(scrollLeft > 0)
      setCanScrollNext(scrollLeft < maxScrollLeft)
    }

    tabContainerElement.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      tabContainerElement.removeEventListener('scroll', handleScroll)
    }
  }, [!!focusNode])

  const startTabNavigation = useCallback(
    (direction: Direction) => {
      const scrollBy = direction === Direction.Forward ? 198 : -198
      scroll.scrollMore(scrollBy, scrollOptions.TabScroll.tabsNav)

      clearInterval(mouseOverIntervalId.current)
      mouseOverIntervalId.current = window.setInterval(() => {
        startTabNavigation(direction)
      }, 300)
    },
    [tabIndex],
  )

  const stopTabNavigation = () => {
    clearInterval(mouseOverIntervalId.current)
  }

  return {
    canScrollPrev,
    canScrollNext,
    stopTabNavigation,
    forwardTabNavigation: () => startTabNavigation(Direction.Forward),
    backwardTabNavigation: () => startTabNavigation(Direction.Backward),
  }
}
